import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SubscriptionDataViewModel } from '../hooks/useSubscriptionData';
import { SubscriptionActionsViewModel } from '../hooks/useSubscriptionActions';
import SubscriptionLimits from '../SubscriptionLimits';
import DowngradeConfirmModal from './modals/DowngradeConfirmModal';
import UpgradeProrationModal from './modals/UpgradeProrationModal';
import LifetimeTab from './LifetimeTab';
import '../Subscriptions.css';

interface SubscriptionsShellProps {
  data: SubscriptionDataViewModel;
  actions: SubscriptionActionsViewModel;
  renderMonthlyPlans: (props: {
    activeTab: string;
    activeTier: string;
    activeBillingCycle: string | null;
    hasPaidSubscription: boolean;
    hasRoleBasedAccess: boolean;
    hasPendingDowngrade: boolean;
    subscriptionStatus: any;
    unifiedPlans: any[];
    tierLimits: Map<string, any>;
    paypalSdkLoaded: boolean;
    upgradeLoading: string | null;
    downgradeLoading: string | null;
    onUpgrade: (planId: string) => void;
    onDowngrade: (planId: string) => void;
    fetchSubscriptionData: () => Promise<void>;
    hasPendingCancellationToFree?: boolean;
    isFamilyMember: boolean;
  }) => React.ReactNode;
  renderAnnualPlans: (props: {
    activeTab: string;
    activeTier: string;
    activeBillingCycle: string | null;
    hasPaidSubscription: boolean;
    hasRoleBasedAccess: boolean;
    hasPendingDowngrade: boolean;
    subscriptionStatus: any;
    unifiedPlans: any[];
    tierLimits: Map<string, any>;
    paypalSdkLoaded: boolean;
    upgradeLoading: string | null;
    downgradeLoading: string | null;
    onUpgrade: (planId: string) => void;
    onDowngrade: (planId: string) => void;
    fetchSubscriptionData: () => Promise<void>;
    isFamilyMember: boolean;
  }) => React.ReactNode;
}

export default function SubscriptionsShell({
  data,
  actions,
  renderMonthlyPlans,
  renderAnnualPlans,
}: SubscriptionsShellProps) {
  const {
    subscriptionStatus,
    subscriptionPlans,
    uploadLimits,
    features,
    lifetimeStatus,
    unifiedPlans,
    tierLimits,
    paypalSdkLoaded,
    paypalClientId,
    syncingSubscription,
    activeTab,
    activeTier,
    activeBillingCycle,
    hasPaidSubscription,
    hasRoleBasedAccess,
    hasPendingDowngrade,
    isFamilyMember,
    handleTabChange,
    fetchSubscriptionData,
  } = data;

  const {
    upgradeLoading,
    downgradeLoading,
    showDowngradeModal,
    setShowDowngradeModal,
    pendingDowngradePlanId: _pendingDowngradePlanId, // eslint-disable-line @typescript-eslint/no-unused-vars
    setPendingDowngradePlanId,
    showUpgradeModal,
    setShowUpgradeModal,
    pendingUpgradePlanId,
    setPendingUpgradePlanId,
    prorationWizardStep,
    setProrationWizardStep,
    upgradeProration,
    setUpgradeProration,
    handleDowngradeClick,
    handleDowngradeConfirm,
    handleUpgrade,
    handleUpgradeConfirm,
  } = actions;

  const handleDowngradeCancel = () => {
    setShowDowngradeModal(false);
    setPendingDowngradePlanId(null);
  };

  const handleUpgradeCancel = () => {
    setShowUpgradeModal(false);
    setPendingUpgradePlanId(null);
    setUpgradeProration(null);
    setProrationWizardStep(1);
  };

  const hasPendingCancellationToFree = subscriptionStatus?.downgrade_pending && 
                                       subscriptionStatus?.pending_tier === 'free';
  
  const planRenderProps = {
    activeTab,
    activeTier,
    activeBillingCycle,
    hasPaidSubscription,
    hasRoleBasedAccess,
    hasPendingDowngrade,
    subscriptionStatus,
    unifiedPlans,
    tierLimits,
    paypalSdkLoaded,
    upgradeLoading,
    downgradeLoading,
    onUpgrade: handleUpgrade,
    onDowngrade: handleDowngradeClick,
    fetchSubscriptionData,
    hasPendingCancellationToFree,
    isFamilyMember,
  };

  return (
    <div className="subscriptions-container p-4 sm:p-6 max-w-full sm:max-w-6xl mx-auto overflow-x-hidden overflow-y-visible">
      {/* Syncing indicator */}
      {syncingSubscription && (
        <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center gap-3">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            Syncing subscription changes from PayPal...
          </p>
        </div>
      )}
      
      {/* Subscription Tier Cards with Tabs */}
      <Card className="mb-6" data-subscription-tiers>
        <CardHeader>
          <CardTitle>Subscription Tiers</CardTitle>
          <CardDescription>
            Choose a subscription plan that fits your needs
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <TabsList className={`grid w-full ${lifetimeStatus ? 'grid-cols-3' : 'grid-cols-2'}`}>
              {lifetimeStatus && (
                <TabsTrigger value="lifetime" className="flex items-center gap-1">
                  Lifetime
                </TabsTrigger>
              )}
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
              <TabsTrigger value="annual">Annual</TabsTrigger>
            </TabsList>

            {/* Lifetime Access Tab */}
            {lifetimeStatus && (
              <TabsContent value="lifetime" className="mt-4">
                <LifetimeTab
                  lifetimeStatus={lifetimeStatus}
                  onPurchaseSuccess={fetchSubscriptionData}
                  tierLimits={tierLimits}
                  paypalClientId={paypalClientId}
                />
              </TabsContent>
            )}

            {/* Monthly Plans Tab */}
            <TabsContent value="monthly" className="mt-4">
              {renderMonthlyPlans(planRenderProps)}
            </TabsContent>

            {/* Annual Plans Tab */}
            <TabsContent value="annual" className="mt-4">
              {renderAnnualPlans(planRenderProps)}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <SubscriptionLimits
        subscriptionStatus={subscriptionStatus}
        uploadLimits={uploadLimits}
        features={features}
        tierLimits={tierLimits}
        countdown={data.countdown}
      />

      {/* Merged Subscription Status and Upload Limits Card */}
      {subscriptionPlans && subscriptionPlans.plans.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>All Available Plans</CardTitle>
            <CardDescription>
              {subscriptionPlans.discount_available 
                ? 'Special discount available! 🎉' 
                : 'Choose a plan that fits your needs'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {subscriptionPlans.plans.map((plan) => (
                <Card key={plan.plan_id} className="relative">
                  {plan.plan_type === 'discounted' && (
                    <Badge className="absolute top-2 right-2" variant="default">
                      Discounted
                    </Badge>
                  )}
                  <CardHeader>
                    <CardTitle className="text-lg capitalize">{plan.tier}</CardTitle>
                    <CardDescription className="capitalize">
                      {plan.billing_cycle} • {plan.plan_type}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-4">
                      <span className="text-3xl font-bold">${plan.price}</span>
                      <span className="text-muted-foreground">
                        /{plan.billing_cycle === 'monthly' ? 'mo' : 'yr'}
                      </span>
                    </div>
                    <Button className="w-full" variant="outline">
                      Select Plan
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modals */}
      <DowngradeConfirmModal
        open={showDowngradeModal}
        onOpenChange={setShowDowngradeModal}
        subscriptionStatus={subscriptionStatus}
        activeTier={activeTier}
        downgradeLoading={downgradeLoading}
        onConfirm={handleDowngradeConfirm}
        onCancel={handleDowngradeCancel}
      />

      <UpgradeProrationModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        upgradeProration={upgradeProration}
        subscriptionStatus={subscriptionStatus}
        pendingUpgradePlanId={pendingUpgradePlanId}
        unifiedPlans={unifiedPlans}
        prorationWizardStep={prorationWizardStep}
        setProrationWizardStep={setProrationWizardStep}
        upgradeLoading={upgradeLoading}
        onConfirm={handleUpgradeConfirm}
        onCancel={handleUpgradeCancel}
      />
    </div>
  );
}
